import { useState } from "react";
import {
  Button,
  FormControl,
  InputAdornment,
  Divider,
  Box,
  Grid,
  OutlinedInput,
  Typography,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Storage } from "aws-amplify";
import SimpleDialog from "src/content/pages/Components/Modals/SimpleDialog";
import Info, { AlertType } from "../Info";
import { getDateAsAFolderName } from "src/content/Utils";

const OutlinedInputWrapper = styled(OutlinedInput)(
  ({ theme }) => `
      background-color: ${theme.colors.alpha.white[100]};
  `,
);

const FileUploader = ({
  execValidationsBeforeUpload = (file: File) => Promise.resolve(true),
  execAfterUpload = null,
  obtainFileData = null,
  keyPath,
  supportedExtensions,
  disableUpload = false,
}) => {
  const [files, setFiles] = useState([]);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [isUploadComplete, setUploadComplete] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  const onFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files).map((file: File) => ({
      file,
      name: file.name,
      progress: 0,
    }));
    setFileErrors([]);
    setFiles(selectedFiles);

    if (obtainFileData) {
      for (const fileObj of selectedFiles) {
        obtainFileData(fileObj.file);
      }
    }
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      setFileErrors(["Please select at least one file."]);
      return;
    }

    const invalidFiles = files.filter(
      ({ name }) =>
        !supportedExtensions.includes(name.split(".").pop().toLowerCase()),
    );

    if (invalidFiles.length > 0) {
      setFileErrors(["Some files have unsupported extensions."]);
      return;
    }

    for (const fileObj of files) {
      const isValid = await execValidationsBeforeUpload(fileObj);
      if (!isValid) return;
    }

    setUploadInProgress(true);
    for (const fileObj of files) {
      const date = getDateAsAFolderName();

      const key = `${keyPath}/${date}/${fileObj.name}`;
      try {
        await Storage.put(key, fileObj.file, {
          level: "public",
          progressCallback(progress) {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.name === fileObj.name
                  ? {
                      ...f,
                      progress: (progress.loaded / progress.total) * 100,
                    }
                  : f,
              ),
            );
          },
        });
      } catch (error) {
        console.error("Error uploading file:", error);
        setFileErrors(["Error uploading some files. Please try again."]);
      }

      if (execAfterUpload) execAfterUpload(fileObj);
    }
    setUploadComplete(true);
    setUploadInProgress(false);
  };

  const handleClose = () => {
    setUploadComplete(false);
    setFiles([]);
    setUploadInProgress(false);
  };

  return (
    <>
      <Typography
        variant="h5"
        color="text.secondary"
        fontWeight="normal"
        marginBottom="1%"
      >
        Please select files to upload:
      </Typography>

      <Grid container justifyContent="flex-end">
        <Grid item>
          <SimpleDialog
            title={"Info"}
            msg={"Files uploaded successfully and are being processed..."}
            open={isUploadComplete}
            onClose={handleClose}
          />
        </Grid>
      </Grid>

      <FormControl variant="outlined" fullWidth>
        <OutlinedInputWrapper
          type="text"
          placeholder={
            files.length
              ? files.map(({ name }) => name).join(", ")
              : "Select files..."
          }
          disabled={true}
          endAdornment={
            <InputAdornment position="end">
              <label htmlFor="upload-files">
                <input
                  style={{ display: "none" }}
                  id="upload-files"
                  name="upload-files"
                  type="file"
                  multiple
                  onChange={onFileChange}
                />
                <Button variant="contained" component="span">
                  Select Files
                </Button>
              </label>
            </InputAdornment>
          }
        />
      </FormControl>

      <Divider sx={{ my: 2 }} />
      {uploadInProgress &&
        files.map(({ name, progress }) => (
          <Box key={name} sx={{ my: 1 }}>
            <Typography variant="body2">{name}</Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        ))}

      {fileErrors.map((error, index) => (
        <Info key={index} message={error} infoType={AlertType.ERROR} />
      ))}

      <Box>
        <Button
          variant="outlined"
          onClick={uploadFiles}
          sx={{ my: 1 }}
          disabled={disableUpload}
        >
          Upload
        </Button>
      </Box>
    </>
  );
};

export default FileUploader;
